import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TableSortLabel,
  IconButton,
  Tooltip
} from '@mui/material';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { get, post } from '../../services/apiService';

/**
 * Daily Execution Tab component
 * Displays test execution records from tests-scheduler collection
 */
const DailyExecutionTab = () => {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPRs, setSelectedPRs] = useState([]);
  const [selectedTests, setSelectedTests] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('prs'); // 'prs' or 'tests'
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('dateTime');
  const [analysisByExecutionId, setAnalysisByExecutionId] = useState({});
  const [analysisLoadingId, setAnalysisLoadingId] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [analysisDialogOpen, setAnalysisDialogOpen] = useState(false);
  const [analysisDialogData, setAnalysisDialogData] = useState(null);

  // Fetch daily executions data (includes cached launchAnalysis)
  const fetchExecutions = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await get('/api/recommended-tests/daily-executions', {
        includeExecuted: 'true'
      });

      if (response.success && response.data) {
        const list = response.data || [];
        setExecutions(list);
        // Hydrate analysis cache from DB so Analysis link shows without re-running
        const byId = {};
        list.forEach((ex) => {
          if (ex.launchAnalysis) byId[ex._id] = ex.launchAnalysis;
        });
        setAnalysisByExecutionId((prev) => ({ ...prev, ...byId }));
      } else {
        throw new Error(response.message || 'Failed to fetch daily executions');
      }
    } catch (err) {
      console.error('Error fetching daily executions:', err);
      setError(err.message || 'Failed to load daily executions');
    } finally {
      setLoading(false);
    }
  };

  // Handle PR link click
  const handlePRClick = (prs) => {
    setSelectedPRs(prs || []);
    setSelectedTests([]);
    setDialogType('prs');
    setDialogOpen(true);
  };

  // Handle Tests link click
  const handleTestsClick = (tests) => {
    setSelectedTests(tests || []);
    setSelectedPRs([]);
    setDialogType('tests');
    setDialogOpen(true);
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedPRs([]);
    setSelectedTests([]);
  };

  // Run (or re-run) analysis: compare unique tests with RP launch, save to DB, update UI
  const handleAnalyseClick = async (execution) => {
    if (execution.rpLaunchId == null) {
      setAnalysisError('No RP Launch ID for this execution.');
      return;
    }
    const uniqueTests = (execution.recommendedTests || []).map((t) => ({
      testPath: t.testPath,
      testName: t.testName,
      testDefinitionId: t.testDefinitionId
    }));
    setAnalysisLoadingId(execution._id);
    setAnalysisError(null);
    try {
      const response = await post('/api/report-portal/launch-analysis', {
        launchId: execution.rpLaunchId,
        uniqueTests,
        executionId: execution._id
      });
      if (response.success && response.data) {
        setAnalysisByExecutionId((prev) => ({ ...prev, [execution._id]: response.data }));
      } else {
        setAnalysisError(response.message || 'Analysis failed');
      }
    } catch (err) {
      console.error('Launch analysis error:', err);
      setAnalysisError(err.message || 'Analysis failed');
    } finally {
      setAnalysisLoadingId(null);
    }
  };

  const handleOpenAnalysisDialog = (data) => {
    setAnalysisDialogData(data);
    setAnalysisDialogOpen(true);
  };

  // Handle sorting
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Sorting comparator
  const descendingComparator = (a, b, orderBy) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];

    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;

    if (orderBy === 'dateTime') {
      aValue = new Date(aValue).getTime();
      bValue = new Date(bValue).getTime();
    }

    if (bValue < aValue) return -1;
    if (bValue > aValue) return 1;
    return 0;
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  // Sort executions
  const sortedExecutions = [...executions].sort(getComparator(order, orderBy));

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled':
        return 'warning';
      case 'Executed':
        return 'success';
      default:
        return 'default';
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchExecutions();
  }, []);

  // Close error snackbar
  const handleCloseError = () => {
    setError(null);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        Daily Test Execution
      </Typography>

      {/* Executions Table */}
      <Paper elevation={2}>
        <TableContainer>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : sortedExecutions.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No daily executions found.
              </Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={orderBy === 'dateTime'}
                      direction={orderBy === 'dateTime' ? order : 'asc'}
                      onClick={() => handleRequestSort('dateTime')}
                    >
                      Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={orderBy === 'prsCount'}
                      direction={orderBy === 'prsCount' ? order : 'asc'}
                      onClick={() => handleRequestSort('prsCount')}
                    >
                      No of PRs Considered
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={orderBy === 'deduplicatedTestsCount'}
                      direction={orderBy === 'deduplicatedTestsCount' ? order : 'asc'}
                      onClick={() => handleRequestSort('deduplicatedTestsCount')}
                    >
                      Executed Tests
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    RP Launch
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={orderBy === 'executionStatus'}
                      direction={orderBy === 'executionStatus' ? order : 'asc'}
                      onClick={() => handleRequestSort('executionStatus')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', width: 100 }}>
                    Completeness %
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', width: 90 }}>
                    Pass %
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', width: 100 }}>
                    Analysis
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', width: 80 }}>
                    Analyse
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedExecutions.map((execution) => {
                  const analysis = analysisByExecutionId[execution._id] ?? execution.launchAnalysis;
                  return (
                  <TableRow key={execution._id} hover>
                    <TableCell>
                      {execution.dateTime
                        ? new Date(execution.dateTime).toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          })
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {execution.prsCount > 0 ? (
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => handlePRClick(execution.prsConsidered)}
                          sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                        >
                          {execution.prsCount}
                        </Link>
                      ) : (
                        execution.prsCount
                      )}
                    </TableCell>
                    <TableCell>
                      {execution.deduplicatedTestsCount > 0 ? (
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => handleTestsClick(execution.recommendedTests)}
                          sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                        >
                          {execution.deduplicatedTestsCount || 0}
                        </Link>
                      ) : (
                        execution.deduplicatedTestsCount || 0
                      )}
                    </TableCell>
                    <TableCell>
                      {execution.rpLaunchId != null ? (
                        <Link
                          href={`${process.env.REACT_APP_REPORT_PORTAL_BASE_URL || 'https://lab1reportportal.highq.com'}/ui/#develop/launches/all/${execution.rpLaunchId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ fontWeight: 500 }}
                        >
                          {execution.rpLaunchId}
                        </Link>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={execution.executionStatus || 'Unknown'}
                        size="small"
                        color={getStatusColor(execution.executionStatus)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {analysis != null && (analysis.completenessPct != null || analysis.matchedInLaunch != null) ? (
                        analysis.completenessPct != null ? `${analysis.completenessPct}%` : '-'
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {analysis != null && (analysis.successPct != null || analysis.passPercentage != null) ? (
                        analysis.successPct != null ? `${analysis.successPct}%` : `${analysis.passPercentage ?? 0}%`
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {execution.rpLaunchId == null ? (
                        '-'
                      ) : analysis ? (
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => handleOpenAnalysisDialog(analysis)}
                          sx={{ cursor: 'pointer', fontWeight: 500 }}
                        >
                          Analysis
                        </Link>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {execution.rpLaunchId == null ? (
                        '-'
                      ) : analysisLoadingId === execution._id ? (
                        <CircularProgress size={24} />
                      ) : (
                        <Tooltip title="Run or refresh analysis: compare unique tests with Report Portal launch">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleAnalyseClick(execution)}
                            aria-label="Run analysis"
                          >
                            <AnalyticsIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Paper>

      {/* PR Details Dialog */}
      <Dialog
        open={dialogOpen && dialogType === 'prs'}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          PRs Considered ({selectedPRs.length})
        </DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>PR Number</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Author</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>State</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Repository</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedPRs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No PRs found
                    </TableCell>
                  </TableRow>
                ) : (
                  selectedPRs.map((pr) => (
                    <TableRow key={pr._id || pr.prNumber}>
                      <TableCell>{pr.prNumber || '-'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {pr.title || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{pr.author || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={pr.state || '-'}
                          size="small"
                          color={pr.state === 'merged' ? 'success' : pr.state === 'open' ? 'info' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={pr.status || '-'}
                          size="small"
                          color={pr.status === 'Executed' ? 'success' : pr.status === 'To be Executed' ? 'warning' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {pr.repositoryId?.githubRepoName || '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Tests Details Dialog */}
      <Dialog
        open={dialogOpen && dialogType === 'tests'}
        onClose={handleDialogClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Executed Tests ({selectedTests.length})
        </DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Test Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Test Path</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Tags</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedTests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No tests found
                    </TableCell>
                  </TableRow>
                ) : (
                  selectedTests.map((test, index) => (
                    <TableRow key={test.testDefinitionId || index}>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {test.testName || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {test.testPath || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {test.tags && Array.isArray(test.tags) && test.tags.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {test.tags.map((tag, tagIndex) => (
                              <Chip
                                key={tagIndex}
                                label={tag}
                                size="small"
                                sx={{ height: '20px', fontSize: '0.7rem' }}
                              />
                            ))}
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Analysis diff dialog: Unique Tests vs Report Portal launch */}
      <Dialog
        open={analysisDialogOpen}
        onClose={() => setAnalysisDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Analysis: Unique Tests vs Report Portal Launch</DialogTitle>
        <DialogContent dividers>
          {analysisDialogData && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Top metrics: backward compat with totalUnique / matchedInLaunch if new fields missing */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
                <Typography variant="body2">
                  <strong>Drill4J Recommended:</strong> {analysisDialogData.drill4JRecommended ?? analysisDialogData.totalUnique ?? 0}
                </Typography>
                <Typography variant="body2">
                  <strong>Test Matched in RP:</strong> {analysisDialogData.testMatchedInRp ?? analysisDialogData.matchedInLaunch ?? 0}
                </Typography>
                <Typography variant="body2">
                  <strong>Test Not Found in RP:</strong> {analysisDialogData.testNotFoundInRp ?? (analysisDialogData.onlyInUnique?.length ?? 0)}
                </Typography>
                <Typography variant="body2">
                  <strong>Completeness %:</strong> {analysisDialogData.completenessPct ?? 0}%
                </Typography>
                <Typography variant="body2">
                  <strong>Success %:</strong> {analysisDialogData.successPct ?? analysisDialogData.passPercentage ?? 0}%
                </Typography>
              </Box>
              <Typography variant="subtitle2" fontWeight="bold">
                Tests not found in RP ({analysisDialogData.onlyInUnique?.length || 0}) — link to ADO test case
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Test Path</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Test Name (ADO)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(analysisDialogData.onlyInUnique || []).map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{row.testPath || '-'}</TableCell>
                        <TableCell>
                          {row.adoUrl ? (
                            <Link
                              href={row.adoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ fontWeight: 500 }}
                            >
                              {row.testName || row.adoWorkItemId || '-'}
                            </Link>
                          ) : (
                            <Typography variant="body2">{row.testName || '-'}</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnalysisDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {analysisError && (
        <Snackbar
          open={!!analysisError}
          autoHideDuration={6000}
          onClose={() => setAnalysisError(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={() => setAnalysisError(null)} severity="error" sx={{ width: '100%' }}>
            {analysisError}
          </Alert>
        </Snackbar>
      )}

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DailyExecutionTab;

